import { Tabs } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TeamMember } from "../../Entities/TeamMember";
import teamMemberService from "../../Service/team-member.service";
import TeamMembersList from "./TeamMembersList";
import { openNotification } from "../../Components/Notification";
import InviteList from "./InviteList";
import userService from "../../Service/user.service";
import useUser from "../../Hooks/useUser";
import TeamMembersPageHeader from "./components/TeamMembersPageHeader";
import InviteTeamMemberModal from "./components/InviteTeamMemberModal";
import { PaginationType, FindAllTeamMembersFilter } from "../../Interface";
import useAccess from "../../Hooks/useAccess";
import SubscriptionWarning from "../../Components/SubscriptionWarning";
import "./index.scss";

const TeamMembers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [inviteSearchQuery, setInviteSearchQuery] = useState<string>("");
  const [inviteModalOpen, setInviteModalOpen] = useState<boolean>(false);
  const [defaultEmail, setDefaultEmail] = useState<string>("");
  const [filters, setFilters] = useState<FindAllTeamMembersFilter>({
    search: null,
    isActive: null,
    role: null,
    sort: null,
  });
  const [pagination, setPagination] = useState<PaginationType>({
    page: 1,
    limit: 10,
    totalPages: 0,
    totalRecords: 0,
  });

  // Get active tab from URL query parameters, default to "1"
  const activeTabKey = searchParams.get("tab") || "1";
  const { currentLimits, currentSubscription, } = useAccess();
  const [open, setOpen] = useState<boolean>(false);
  const { user } = useUser();
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [statistics, setStatistics] = useState<{
    activeMembers: number;
    inactiveMembers: number;
    availableForInterview: number;
    notAvailableForInterview: number;
  } | null>(null);

  const fetchStatistics = async () => {
    try {
      const stats = await userService.getAllMembersStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error("Error fetching statistics:", error);
      setStatistics(null);
    }
  };

  const fetchTeamMembers = useCallback(async () => {
    setIsDataLoading(true);
    try {
      const data = await userService.getAllMembersWithFilters({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search,
        isActive: filters.isActive,
        role: filters.role,
        sort: filters.sort,
      });
      setTeamMembers(data?.users || []);
      setPagination((prev) => ({
        ...prev,
        totalPages: data?.totalPages || 0,
        totalRecords: data?.total || 0,
      }));
    } catch (error) {
      console.error("Error fetching team members:", error);
      setTeamMembers([]);
      setPagination((prev) => ({
        ...prev,
        totalPages: 0,
        totalRecords: 0,
      }));
    } finally {
      setIsDataLoading(false);
    }
  }, [
    pagination.page,
    pagination.limit,
    filters.search,
    filters.isActive,
    filters.role,
    filters.sort,
  ]);

  useEffect(() => {
    fetchStatistics();
  }, []);

  // Set default tab in URL if not present (run once on mount)
  useEffect(() => {
    if (!searchParams.get("tab")) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("tab", "1");
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once and avoid infinite loop

  const handlePageChange = (page: number, size?: number) => {
    setPagination((prev) => ({
      ...prev,
      page,
      limit: size || prev.limit,
    }));
  };

  const updateFilters = (
    key: keyof FindAllTeamMembersFilter,
    value: any | null,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    // Reset pagination when filters change
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handleSearch = (query: string) => {
    if (activeTabKey === "1") {
      updateFilters("search", query);
    } else {
      setInviteSearchQuery(query);
    }
  };

  const handleClearAll = () => {
    setFilters({
      search: null,
      isActive: null,
      role: null,
      sort: null,
    });
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  const handleInviteUser = async (values: { email: string }) => {
    try {
      const response = await teamMemberService.inviteUserToOrg(values.email);
      if (response) {
        openNotification("success", "Invitation sent successfully", "");
      }
    } catch (error: any) {
      openNotification(
        "error",
        "Invitation failed",
        error?.response?.data?.message,
      );
      console.error("Error inviting user:", error);
    }
    setInviteModalOpen(false);
  };

  const openInviteModal = (email: string | undefined) => {
    if (
      currentLimits?.teamMembers &&
      currentSubscription &&
      currentLimits?.teamMembers !== -1
    ) {
      if (
        currentSubscription?.measurements.teamMembers >=
        currentLimits?.teamMembers
      ) {
        setOpen(true);
        return;
      }
    }
    if (email) {
      setDefaultEmail(email);
    }
    setInviteModalOpen(true);
  };

  const handleBlockAndUnblock = async (teamMember: any) => {
    const status = teamMember.status === "Active" ? "block" : "unblock";

    try {
      const response: any = await userService.blockOrUnblockUser(
        teamMember.key,
        status,
      );
      if (response !== null) {
        openNotification("success", `Team Member ${status}ed successfully`, "");

        const updatedTeamMembers = teamMembers.map((member) => {
          if (member._id === teamMember.key) {
            return { ...member, ...response };
          }
          return member;
        });

        setTeamMembers(updatedTeamMembers);

        // Update statistics locally
        if (statistics) {
          const updatedStatistics = { ...statistics };

          if (status === "block") {
            // User becomes inactive
            updatedStatistics.activeMembers = Math.max(
              0,
              updatedStatistics.activeMembers - 1,
            );
            updatedStatistics.inactiveMembers =
              updatedStatistics.inactiveMembers + 1;

            // When blocking, if user was available for interviews, they become unavailable
            // When blocking, if user was not available, the notAvailable count stays the same
            if (teamMember.isAvailableForInterview) {
              updatedStatistics.availableForInterview = Math.max(
                0,
                updatedStatistics.availableForInterview - 1,
              );
            } else {
              updatedStatistics.notAvailableForInterview = Math.max(
                0,
                updatedStatistics.notAvailableForInterview - 1,
              );
            }
          } else {
            // User becomes active (unblock)
            updatedStatistics.inactiveMembers = Math.max(
              0,
              updatedStatistics.inactiveMembers - 1,
            );
            updatedStatistics.activeMembers =
              updatedStatistics.activeMembers + 1;

            // Check if unblocked user is available for interviews from the API response
            const updatedMember = updatedTeamMembers.find(
              (m) => m._id === teamMember.key,
            );
            if (updatedMember?.isAvailableForInterviews) {
              updatedStatistics.availableForInterview =
                updatedStatistics.availableForInterview + 1;
            } else {
              // User is active but not available for interviews
              updatedStatistics.notAvailableForInterview =
                updatedStatistics.notAvailableForInterview + 1;
            }
          }

          setStatistics(updatedStatistics);
          console.log(
            "🚀 ~ handleBlockAndUnblock ~ updated statistics:",
            updatedStatistics,
          );
        }
      }
    } catch (error) {
      console.error(`Error ${status}ing team member:`, error);
    }
  };

  const handleTabChange = (key: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("tab", key);
    setSearchParams(newSearchParams);
  };

  const renderTeamMembers = () => (
    <TeamMembersList
      teamMembers={teamMembers}
      pagination={pagination}
      handlePageChange={handlePageChange}
      handleBlockAndUnblock={handleBlockAndUnblock}
      isLoading={isDataLoading}
      statistics={statistics}
      filters={filters}
      updateFilters={updateFilters}
      onSearch={handleSearch}
      onClearAll={handleClearAll}
    />
  );

  const renderInvites = () => (
    <InviteList
      handleInviteAgain={openInviteModal}
      searchQuery={inviteSearchQuery}
    />
  );

  const items = [
    {
      key: "1",
      label: "Team Members",
    },
  ];

  if (user && user.role === "admin") {
    items.push({
      key: "2",
      label: "Invites",
    });
  }

  const getTabChildren = (tab: string) => {
    if (tab === "1") {
      return renderTeamMembers();
    } else if (tab === "2") {
      return renderInvites();
    }
  };

  return (
    <section className="team-members-page">
      <TeamMembersPageHeader openInviteModal={openInviteModal} />
      <Tabs
        activeKey={activeTabKey}
        items={items}
        onChange={handleTabChange}
        size="small"
        style={{ margin: "0px", width: "100%", marginBottom: "16px", overflowY: "auto", height: "max-content" }}
      />
      <div className="team-members-page-tab-children-wrapper">
        {getTabChildren(activeTabKey)}
      </div>
      <InviteTeamMemberModal
        inviteModalOpen={inviteModalOpen}
        setInviteModalOpen={setInviteModalOpen}
        handleInviteUser={handleInviteUser}
        defaultEmail={defaultEmail}
      />
      <SubscriptionWarning
        open={open}
        handleClose={() => setOpen(false)}
        title="Limit reached"
        type="team-member-limit"
      />
    </section>
  );
};

export default TeamMembers;
